import { useEffect } from "react";
import { useLocation } from "react-router-dom";
// Data lives in its own module so the build can read it — see routeMeta.js for
// why a React effect alone cannot fix a shared social card.
import {
  INDEXABLE_PATHS,
  ROUTE_META,
  isDailyAbsiteEnabled,
} from "../../constants/routeMeta";

const DAILY_ABSITE_PATH = "/daily-absite-question";

// Technical-SEO baseline (contract §5.S3, LEG-1). The QLearn app is the
// CONVERSION surface, not the SEO surface (that is medlearnity.com/qlearn/).
// Only the brand/navigational entry points should be indexable; everything
// else is authed or app-internal and must not land in Google's index.
//
// INDEXABLE_PATHS is an allow-list of EXACT pathnames. The bare root serves
// the login/landing to anonymous visitors (crawlers), so both `/` and
// `/login` stay indexable; every other route emits robots=noindex.
//
// `/daily-absite-question` is the third entry and the only non-brand one: it is
// a PUBLIC ACQUISITION page (issue #310 — "public landing path for a daily
// ABSITE practice question", built to receive organic, social and paid-social
// traffic). Under the original two-entry allow-list it emitted noindex, so the
// one page built to be found was the one page telling crawlers not to find it.
// That is a bug relative to the page's purpose, not a policy exception: the
// "app is the conversion surface, not the SEO surface" rule still holds for
// every authed and app-internal route.
export { INDEXABLE_PATHS, ROUTE_META };

// Route-specific head metadata for the public acquisition surfaces. Generic
// app-shell metadata is fine for a login screen and actively costly on a
// landing page: it is the title in a search result and the card in a shared
// link, so it drives click-through on exactly the traffic we pay for (issue
// #313). Only routes listed here are overridden; everything else keeps the
// app-shell tags authored in public/index.html.
//
// Keep descriptions under ~155 chars — past that Google truncates mid-sentence.

// Pure: the metadata overrides for a pathname, or null when the route keeps the
// app-shell defaults. Trailing slashes normalize like getRobotsForPath.
export const getRouteMeta = (pathname) => {
  const normalized = normalizePath(pathname);
  if (normalized === DAILY_ABSITE_PATH && !isDailyAbsiteEnabled()) return null;
  return ROUTE_META[normalized] || null;
};

// Pure: does this pathname get a `<meta name="robots" content="noindex">`?
// Returns the robots content string, or null when the route is indexable.
// Trailing slashes are normalized so `/exam/` and `/exam` behave the same.
// Strip trailing slashes so `/exam/` and `/exam` resolve identically, using a
// simple loop instead of a backtracking regex. The bare root is left as `/`;
// any other all-slashes path reduces to "" — matching the original
// `replace(/\/+$/, "")` behavior exactly.
export const normalizePath = (pathname) => {
  let normalized = pathname == null ? "" : String(pathname);
  if (normalized !== "/") {
    while (normalized.endsWith("/")) {
      normalized = normalized.slice(0, -1);
    }
  }
  return normalized;
};

export const getRobotsForPath = (pathname) => {
  const normalized = normalizePath(pathname);
  if (normalized === DAILY_ABSITE_PATH && !isDailyAbsiteEnabled()) return "noindex";
  return INDEXABLE_PATHS.includes(normalized) ? null : "noindex";
};

const ROBOTS_SELECTOR = 'meta[name="robots"][data-route-meta="1"]';

// Imperatively reconcile the robots meta tag with the current route. There is
// no react-helmet-class dependency in this repo, so we manage the single tag
// on document.head directly (idempotent: one tagged node, added/removed as the
// route changes). The `data-route-meta` marker keeps us from ever touching a
// robots tag authored elsewhere.
export const applyRobotsMeta = (pathname, doc) => {
  const head = doc.head || doc.getElementsByTagName("head")[0];
  if (!head) return;
  const robots = getRobotsForPath(pathname);
  let tag = head.querySelector(ROBOTS_SELECTOR);
  if (robots) {
    if (!tag) {
      tag = doc.createElement("meta");
      tag.setAttribute("name", "robots");
      tag.dataset.routeMeta = "1"; // -> data-route-meta="1"
      head.appendChild(tag);
    }
    tag.setAttribute("content", robots);
  } else if (tag) {
    tag.remove();
  }
};

// The head tags a route may override. `title` is the element; the rest are
// meta/link tags keyed by the attribute that identifies them. Open Graph and
// Twitter mirror the same values — a shared link and a search result should not
// disagree about what the page is.
const MANAGED_TAGS = [
  { key: "description", selector: 'meta[name="description"]', attr: "content" },
  { key: "description", selector: 'meta[property="og:description"]', attr: "content" },
  { key: "description", selector: 'meta[name="twitter:description"]', attr: "content" },
  { key: "title", selector: 'meta[property="og:title"]', attr: "content" },
  { key: "title", selector: 'meta[name="twitter:title"]', attr: "content" },
  { key: "canonical", selector: 'link[rel="canonical"]', attr: "href" },
  { key: "canonical", selector: 'meta[property="og:url"]', attr: "content" },
];

// Originals are captured the FIRST time a tag is overridden and restored when a
// route without overrides is reached. Without this, one visit to the landing
// page would leave its title and canonical on every subsequent route for the
// rest of the session — an SPA head bug that looks fine on the page that set it
// and wrong everywhere after. Captured lazily (not at module load) so it works
// no matter when the manager first mounts.
const originals = new Map();

const rememberOriginal = (selector, current) => {
  if (!originals.has(selector)) originals.set(selector, current);
};

export const applyRouteMeta = (pathname, doc) => {
  const head = doc.head || doc.getElementsByTagName("head")[0];
  if (!head) return;
  const meta = getRouteMeta(pathname);

  if (meta && meta.title) {
    rememberOriginal("title", doc.title);
    doc.title = meta.title;
  } else if (originals.has("title")) {
    doc.title = originals.get("title");
  }

  MANAGED_TAGS.forEach(({ key, selector, attr }) => {
    const tag = head.querySelector(selector);
    // Absent tags are left absent: this reconciles the app-shell head, it does
    // not author a new one. index.html owns which tags exist.
    if (!tag) return;
    if (meta && meta[key]) {
      rememberOriginal(selector, tag.getAttribute(attr));
      tag.setAttribute(attr, meta[key]);
    } else if (originals.has(selector)) {
      const original = originals.get(selector);
      if (original == null) tag.removeAttribute(attr);
      else tag.setAttribute(attr, original);
    }
  });
};

// Test seam: the originals map is module state, and a suite that renders several
// routes needs a clean slate between cases.
export const __resetRouteMetaOriginals = () => originals.clear();

// Mount once inside <Router>; re-applies on every navigation.
const RouteMetaManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    applyRobotsMeta(pathname, document);
    applyRouteMeta(pathname, document);
  }, [pathname]);
  return null;
};

export default RouteMetaManager;
